import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import BrandSwitcher from './BrandSwitcher';
import ConsistencyLogo from './ConsistencyLogo';
import {
  LayoutDashboard,
  Upload,
  Radio,
  Scissors,
  Zap,
  Calendar,
  Sparkles,
  Settings,
  CreditCard,
  Users,
  Shield,
  LogOut,
  Menu,
  X,
  Link as LinkIcon,
  DollarSign,
  MessageSquare,
  MessageCircle,
  BarChart3,
  HardDrive
} from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardLayout = () => {
  const { user, logout } = React.useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [clipEngineEnabled, setClipEngineEnabled] = useState(true);
  const [optimizerEnabled, setOptimizerEnabled] = useState(true);

  useEffect(() => {
    checkModuleSettings();
  }, []);

  const checkModuleSettings = async () => {
    try {
      const response = await axios.get(`${API}/organizations`, { withCredentials: true });
      if (response.data.length > 0) {
        const org = response.data[0];
        setClipEngineEnabled(org?.settings?.clip_engine_enabled !== false);
        setOptimizerEnabled(org?.settings?.optimizer_enabled !== false);
      }
    } catch (error) {
      console.error('Failed to check module settings:', error);
    }
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/brands', icon: Sparkles, label: 'Brands' },
    { path: '/upload', icon: Upload, label: 'Upload' },
    { path: '/live', icon: Radio, label: 'Live' },
  ];

  if (clipEngineEnabled) {
    navItems.push({ path: '/clips', icon: Scissors, label: 'Clips' });
  }

  if (optimizerEnabled) {
    navItems.push({ path: '/optimizer', icon: Zap, label: 'Optimizer' });
  }

  navItems.push(
    { path: '/scheduler', icon: Calendar, label: 'Scheduler' },
    { path: '/intelligence', icon: Sparkles, label: 'Intelligence' },
    { path: '/community', icon: MessageCircle, label: 'Community' },
    { path: '/analytics', icon: BarChart3, label: 'Analytics' }
  );

  // Agency Portfolio link for Agency and Admin users
  if (user?.plan_tier === 'agency' || user?.plan_tier === 'agency_elite' || user?.role === 'admin') {
    navItems.push({ path: '/agency-portfolio', icon: Users, label: 'Agency Portfolio' });
  }

  navItems.push(
    { path: '/social-accounts', icon: LinkIcon, label: 'Social Accounts' },
    { path: '/support/tickets', icon: MessageSquare, label: 'Support' },
    { path: '/settings', icon: Settings, label: 'Settings' },
    { path: '/billing', icon: CreditCard, label: 'Billing' }
  );

  if (user?.role === 'admin') {
    navItems.push(
      { path: '/admin', icon: Shield, label: 'Admin' },
      { path: '/admin/brandkit', icon: Sparkles, label: 'BrandKit' },
      { path: '/admin/pricing-settings', icon: DollarSign, label: 'Pricing Settings' },
      { path: '/self-marketing', icon: Sparkles, label: 'Self-Marketing' },
      { path: '/admin/landing-page', icon: LayoutDashboard, label: 'Landing Page' },
      { path: '/admin/founder-manual', icon: Users, label: 'Founder Manual' }
    );
  }

  // Internal Dashboard for internal admins only
  if (user?.is_internal_admin) {
    navItems.push({ path: '/internal-dashboard', icon: LayoutDashboard, label: 'Internal Dashboard' });
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen bg-[#121417]">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } glass-strong border-r border-white/10 sidebar-transition flex flex-col relative z-50`}
      >
        {/* Logo */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            {sidebarOpen ? (
              <ConsistencyLogo variant="full" size="md" />
            ) : (
              <ConsistencyLogo variant="symbol" size="md" />
            )}
          </Link>
          <button
            data-testid="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Brand Switcher */}
        {sidebarOpen && <BrandSwitcher />}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-[#3A7BFF] text-white shadow-lg shadow-[#3A7BFF]/30'
                    : 'text-[#8A93A6] hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon size={20} />
                {sidebarOpen && <span className="font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3A7BFF] to-[#39E6C8] flex items-center justify-center text-white font-semibold">
              {user?.name?.[0] || 'U'}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-[#8A93A6] truncate">{user?.email}</p>
              </div>
            )}
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-[#FF4F4F] hover:bg-[#FF4F4F]/10 transition-colors"
          >
            <LogOut size={20} />
            {sidebarOpen && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default DashboardLayout;
